import React from "react";
import { Box, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useCreditsBalance from "../../hooks/useCreditsBalance";
import CoinIcon from "../../assets/svgs/CoinIcon";
import { useAppSelector } from "../../redux/typedHooks";
import type { UserInterface } from "../../types/user";

const useStyles = makeStyles(theme => ({
  creditsBadge: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    padding: "6px 12px",
    minHeight: "38.5px",
    borderRadius: "20px",
    // border: "1px solid",
    // backgroundColor: "rgba(0, 0, 0, 0.2)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    textDecoration: "none",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      padding: "4px 8px",
      gap: "4px",
    },
  },
  normal: {
    // borderColor: "#FF68D5",
    color: "#fff",
  },
  low: {
    borderColor: "#ffc107",
    color: "#ffc107",
  },
  critical: {
    borderColor: "#dc3545",
    color: "#dc3545",
  },
  creditsIcon: {
    display: "flex",
    alignItems: "center",
    gap: "6.5px",
    "& svg": {
      width: "16px",
      height: "16px",
      [theme.breakpoints.down("sm")]: {
        width: "14px",
        height: "14px",
      },
    },
  },
  creditsText: {
    fontSize: "12px",
    color: "#FFFFFFB2",
    fontWeight: 400,
    textTransform: "uppercase",
    fontFamily: "Roboto, sans-serif",
  },
  creditsAmount: {
    color: "inherit",
    fontSize: "20px",
    fontWeight: 400,
    fontFamily: "Roboto, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  loadingText: {
    fontSize: "12px",
    color: "#999",
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  errorText: {
    fontSize: "12px",
    color: "#ff4444",
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
}));

const CreditsBalanceBadge: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { balance, loading, error, creditsRemaining } = useCreditsBalance();
  const currentUser = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );

  // Check if user is Creator
  const isCreator = currentUser?.userType?.title === "creator" || 
    currentUser?.userType?._id === "6800a63485ec077c7c266c25";

  const getBadgeClass = () => {
    // Don't show critical/low styling for Creators or when credits = 0
    // (messages will only be shown when Viewer tries to send with 0 credits)
    if (isCreator || creditsRemaining === 0) return classes.normal;
    if (creditsRemaining <= 3) return classes.low;
    return classes.normal;
  };

  const getAlertText = () => {
    // Don't show "No credits!" or "Low credits!" for Creators or when credits = 0
    // (messages will only be shown when Viewer tries to send with 0 credits)
    if (isCreator || creditsRemaining === 0) return "Credits";
    if (creditsRemaining <= 3) return "Low credits!";
    return "Credits";
  };

  const handleNavigateToPacks = () => {
    history.push("/subscriptions");
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavigateToPacks();
    }
  };

  if (loading && !balance) {
    return (
      <Box
        className={`${classes.creditsBadge} ${classes.normal}`}
        role="button"
        tabIndex={0}
        onClick={handleNavigateToPacks}
        onKeyDown={handleKeyPress}
      >
        <Typography className={classes.creditsText}>Credits</Typography>
        <Typography className={classes.loadingText}>Loading...</Typography>
      </Box>
    );
  }

  if (error && !balance) {
    return (
      <Box
        className={`${classes.creditsBadge} ${classes.critical}`}
        role="button"
        tabIndex={0}
        onClick={handleNavigateToPacks}
        onKeyDown={handleKeyPress}
      >
        <Typography className={classes.creditsText}>Credits</Typography>
        <Typography className={classes.errorText}>Unavailable</Typography>
      </Box>
    );
  }

  if (!balance) {
    return null;
  }

  return (
    <Box
      className={`${classes.creditsBadge} ${getBadgeClass()}`}
      onClick={handleNavigateToPacks}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyPress}
    >
      <Typography className={classes.creditsText}>{getAlertText()}</Typography>
      <Box className={classes.creditsIcon}>
        <CoinIcon />

        <Typography className={classes.creditsAmount}>
          {creditsRemaining}
        </Typography>
      </Box>
    </Box>
  );
};

export default CreditsBalanceBadge;
